import { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Grid,
  GridItem,
  VStack,
  Text,
  Button,
  Image,
  Flex,
  Spinner,
  Container,
  Icon,
  Badge,
  HStack,
} from "@chakra-ui/react";
import {
  IoLocationOutline,
  IoCallOutline,
  IoInformationCircleOutline,
  IoBusinessOutline,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";

import ApiResponse from "../../services/ApiResponse";
import { Institucion } from "../../services/models/Institucion";
import config from "../../MapConfig";
import ServerErrorPage from "../ServerErrorPage";
import UnauthorizedPage from "../UnauthorizedPage";

const InstitucionesPage = () => {
  const endpoint = "instituciones";
  const [response, setResponse] = useState(new ApiResponse<Institucion[]>());
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInstituciones = async () => {
      const apiResponse = new ApiResponse<Institucion[]>();
      await apiResponse.useFetch(`${endpoint}`, "GET");
      setResponse(apiResponse);
    };
    fetchInstituciones();
  }, []);

  // Estilo Glassmorphism coherente con el resto de la app
  const glassStyle = {
    bg: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(12px)",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    borderRadius: "2xl",
    transition: "all 0.3s ease-in-out",
  };

  if (response.loading)
    return (
      <Flex justify="center" align="center" height="100vh" bg="blue.900">
        <Spinner size="xl" thickness="4px" speed="0.65s" color="#E9681F" />
      </Flex>
    );

  if (response.responseCode === 401) return <UnauthorizedPage />;
  if (response.responseCode !== 200 && !response.loading)
    return <ServerErrorPage />;

  return (
    <Box bgGradient="linear(to-b, blue.800, #0a192f)" minH="100vh" pb={20}>
      <Container maxW="container.xl" pt={10}>
        {/* Encabezado */}
        <VStack spacing={4} textAlign="center" mb={16} color="white">
          <Badge
            bg="#E9681F"
            color="white"
            px={4}
            py={1}
            borderRadius="full"
            mb={2}
          >
            Goya, Corrientes
          </Badge>
          <Heading size="2xl" fontWeight="black" letterSpacing="tight">
            Instituciones Educativas
          </Heading>
          <Text fontSize="xl" opacity={0.8} maxW="2xl">
            Descubrí los centros de formación académica que impulsan el
            desarrollo de la región.
          </Text>
        </VStack>

        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          }}
          gap={8}
        >
          {response.data?.map((inst) => (
            <GridItem key={inst.id}>
              <Box
                {...glassStyle}
                overflow="hidden"
                _hover={{
                  transform: "translateY(-8px)",
                  borderColor: "#E9681F",
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)",
                }}
              >
                {/* Contenedor del Logo */}
                <Flex
                  bg="white"
                  p={6}
                  justify="center"
                  align="center"
                  h="150px"
                >
                  <Image
                    src={
                      inst.logo
                        ? `${config.API_IMAGES_URL}${
                            inst.logo.startsWith("/")
                              ? inst.logo
                              : `/${inst.logo}`
                          }`
                        : undefined
                    }
                    alt={inst.nombre}
                    maxH="110px"
                    objectFit="contain"
                    fallback={
                      <Icon
                        as={IoBusinessOutline}
                        boxSize={10}
                        color="gray.300"
                      />
                    }
                  />
                </Flex>

                {/* Contenido de la Tarjeta */}
                <Box p={6}>
                  <VStack align="stretch" spacing={4}>
                    <Box minH="80px">
                      <Text
                        color="#E9681F"
                        fontWeight="bold"
                        fontSize="xs"
                        mb={1}
                        textTransform="uppercase"
                      >
                        {inst.gestion || "Educación Superior"}
                      </Text>
                      <Heading
                        size="md"
                        color="white"
                        noOfLines={2}
                        lineHeight="1.2"
                      >
                        {inst.nombre}
                      </Heading>
                    </Box>

                    <VStack
                      align="stretch"
                      spacing={2}
                      borderTop="1px solid"
                      borderColor="whiteAlpha.100"
                      pt={4}
                    >
                      <HStack spacing={3} color="whiteAlpha.700">
                        <Icon as={IoLocationOutline} color="#E9681F" />
                        <Text fontSize="sm" noOfLines={1}>
                          {inst.direccion}
                        </Text>
                      </HStack>

                      {inst.tel && (
                        <HStack spacing={3} color="whiteAlpha.700">
                          <Icon as={IoCallOutline} color="#E9681F" />
                          <Text fontSize="sm">{inst.tel}</Text>
                        </HStack>
                      )}
                    </VStack>

                    {/* Acción Única */}
                    <Button
                      mt={2}
                      w="full"
                      bg="#E9681F"
                      color="white"
                      _hover={{ bg: "#d15a1a" }}
                      leftIcon={<IoInformationCircleOutline />}
                      borderRadius="xl"
                      onClick={() =>
                        navigate(`/instituciones/${inst.id}`, { state: inst })
                      }
                    >
                      Ver Detalles
                    </Button>
                  </VStack>
                </Box>
              </Box>
            </GridItem>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default InstitucionesPage;
